import { useCallback, useContext, useEffect, useState } from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import { useFetchTableMetaConfigs, useSubmitApp } from "@lhd/hooks";
import { getCompanyId, handleColumns } from "@lhd/utility";
import { notification } from "antd";
import { useFormik } from "formik";
import { useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormActionContext } from ".";
import { ListColorPicker } from "./components/AppFormInformation";
import { validationAppInformation } from "./components/AppFormInformation/schema";
import { APP_PART_TYPES, EMPTY_TABLE_MSG } from "./constants";
import { AppPartType } from "./types";
import { useConfirmingLeave } from "@lhd/app/index";
import { handleMessageError } from "../utils";
import { useFetchRecord } from "@lhd/hooks";
import { useMatch } from "react-router-dom";
import { flatten } from "lodash";
import axiosInstance from "@lhd/libs/axios";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { BUTTON_HEIGHT, FONT_SIZE, FONT_SIZE_VALUE } from "@lhd/constants";
import { DISPLAY_SIZES } from "@lhd/types";

export type ReceivedProps = Record<string, any>;

const useFormAction = () => {
  return {};
};

export type Props = ReturnType<typeof useFormAction>;

const DEFAULT_TAB = "app-information";

export const useAppFormCreate = () => {
  const [activeKey, setActiveKey] = useState<string>(DEFAULT_TAB);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParams = searchParams.get("tab");
  const { contextState } = useContext(FormActionContext);
  const { mutate, isLoading: isSubmitting } = useSubmitApp();
  const { setConfirmingState } = useConfirmingLeave();
  const navigate = useNavigate();
  const tableConfigs = useFetchTableMetaConfigs();

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const initialValues = {
    name: "",
    categories: [],
    app_pictures: [],
    picture: null,
    hex_color: ListColorPicker[0],
    description: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationAppInformation,
    onSubmit: () => {},
  });

  const { values } = formik;

  const buildAppPartParams = useCallback((layout: AppPartType[]) => {
    return layout.map(({ item_config_data, children, ...part }) => {
      const config = { ...item_config_data };
      if (
        (item_config_data.preset_workers &&
          item_config_data.preset_workers.length === 0) ||
        item_config_data.preset_worker === false
      ) {
        config.h = 2;
      }
      if (typeof part?.referenced_type === "number") {
        part.copy_from_app_part_id = part.referenced_type;
        delete part.referenced_type;
      }
      return {
        ...part,
        label_text: part.name,
        name: part.name,
        item_config_data: JSON.stringify(config),
        children: children?.map((c) => ({
          ...c,
          item_config_data: JSON.stringify(c.item_config_data),
        })),
      };
    });
  }, []);

  const onFinish = useCallback(() => {
    if (!values.name) {
      notification["error"]({ message: "Bizシート名を入力してください。" });
      return;
    } else if (!values.categories || values.categories.length === 0) {
      notification["error"]({ message: "カテゴリを選択してください。" });
      return;
    }

    if (contextState.layout && Array.isArray(contextState.layout)) {
      let notificationError;
      contextState.layout.forEach((appItem, idx) => {
        if (
          appItem.app_part_type === APP_PART_TYPES.TABLE &&
          !appItem.children?.length
        ) {
          notificationError = EMPTY_TABLE_MSG;
          return;
        }
        if (
          appItem.app_part_type === APP_PART_TYPES.TABLE &&
          appItem.children &&
          Array.isArray(appItem.children)
        ) {
          appItem.children.forEach((appItemChildren) => {
            if (
              [
                APP_PART_TYPES.DATE_AND_TIME,
                APP_PART_TYPES.DATE,
                APP_PART_TYPES.NUMBER,
                APP_PART_TYPES.LINK,
              ].includes(appItemChildren?.app_part_type as any) &&
              appItemChildren?.is_prohibit_duplicate_values
            ) {
              notificationError = `テーブル内のフィールドは値の重複を禁止できません。`;
              return;
            }
          });
        } else if (
          [
            APP_PART_TYPES.RADIO_BUTTON,
            APP_PART_TYPES.CHECK_BOX,
            APP_PART_TYPES.MULTI_CHOICE,
            APP_PART_TYPES.DROPDOWN,
          ].includes(appItem.app_part_type as any)
        ) {
          const listOptions = appItem?.item_config_data?.options ?? [];
          const listOptionsExist: string[] = [];
          listOptions.forEach((optionItem) => {
            if (listOptionsExist.includes(String(optionItem.text))) {
              notificationError = `オプションが重複しています`;
              return;
            } else {
              listOptionsExist.push(String(optionItem.text));
            }
          });
        }
        return;
      });
      if (notificationError) {
        notification["error"]({ message: notificationError });
        return;
      }
    }

    const formData = new FormData();

    formData.append("name", values.name);
    formData.append("hex_color", values.hex_color);
    formData.append(
      "description",
      values.description ? values.description : "",
    );

    if (values.picture) {
      formData.append("picture", values.picture as any);
    }

    if (values.categories && values.categories.length > 0) {
      values.categories.forEach((selectedCategory: number) =>
        formData.append("categories[][id]", String(selectedCategory)),
      );
    }

    if (values.app_pictures) {
      const listAppPicturesUrlExist: string[] = [];
      values.app_pictures.forEach((pictureItem: any) => {
        if (!pictureItem?.url) {
          formData.append("app_pictures[][picture]", pictureItem);
        } else {
          // get list url picture exist (not include deleted)
          listAppPicturesUrlExist.push(pictureItem.url);
        }
      });
    }

    formData.append(
      "app_parts_data",
      JSON.stringify(buildAppPartParams(contextState.layout)),
    );
    formData.append("company_id", getCompanyId());

    mutate(
      {
        payload: formData,
      },
      {
        onSuccess(e: any) {
          setConfirmingState(null);

          const newAppPart = e?.data?.app_parts;
          if (newAppPart && newAppPart?.length > 0) {
            const columnChanges = newAppPart?.map(
              (columnItem: AppPartType) => ({
                columnId: String(columnItem?.id),
                width: columnItem?.width,
              }),
            );
            axiosInstance
              .post(`/apps/${e?.data?.id}/save_records`, {
                company_id: getCompanyId(),
                app_records_data: [],
                meta_config: {
                  table_name: "app_sheets",
                  config: {
                    ...tableConfigs?.data?.app_sheets,
                    [String(e?.data?.id)]: {
                      ...handleColumns(columnChanges),
                      [getCompanyId()]: columnChanges.map(
                        (item: any) => item.columnId,
                      ),
                    },
                  },
                },
              })
              .then(() => navigate(`/apps/${e?.data?.id}/app-table`));
          } else {
            navigate(`/apps/${e?.data?.id}/app-table`);
          }
        },
        onError(e: any) {
          notification["error"]({
            message: handleMessageError(e?.response?.data?.error),
          });
        },
      },
    );
  }, [
    values,
    contextState.layout,
    buildAppPartParams,
    mutate,
    setConfirmingState,
    navigate,
    tableConfigs?.data?.app_sheets,
  ]);

  const onChange = (key: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", key);
      return params;
    });
  };

  useEffect(() => {
    if (!tabParams) {
      setSearchParams({ tab: DEFAULT_TAB });
    } else {
      setActiveKey(tabParams);
    }
  }, [tabParams, setSearchParams]);

  return {
    onFinish,
    onChange,
    DEFAULT_TAB,
    activeKey,
    isSubmitting,
    formik,
    displaySize,
    height,
    size,
  };
};

export const useGetScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [sizeOfSection, setSizeOfSection] = useState({ width: 0, height: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);
    };
    if (sectionRef.current) {
      setSizeOfSection({
        width: sectionRef.current.clientWidth,
        height: sectionRef.current.clientHeight,
      });
    }
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return { scrollPosition, sectionRef, sizeOfSection };
};

export const useGetListDataExist = () => {
  const matchAppGrid = useMatch("/apps/:id/app-grid");
  const matchAppTable = useMatch("/apps/:id/app-table");
  const appId = matchAppGrid?.params?.id || matchAppTable?.params?.id;
  const { data } = useFetchRecord(appId || "");
  const listRecordValue =
    data?.data?.map((record) => record.app_record_values) || [];
  const listAppPartWithData =
    flatten(listRecordValue).filter((recordData) => !!recordData.value) || [];
  const dataExistList = listAppPartWithData.map((ap) =>
    ap.app_part_id?.toString(),
  );
  return { dataExistList };
};

export default useFormAction;
